import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import './CharacterFilmConnections.css';

const NAMES = { notebooklm: 'NotebookLM', flowkit: 'Flowkit', opencut: 'OpenCut', claude: 'Claude', codex: 'Codex', gemini: 'Gemini' } as const;
const LABELS = { connected: 'Connected', authenticated: 'Signed in', configured: 'Configured', unavailable: 'Unavailable' } as const;
type Status = keyof typeof LABELS;
type ServiceId = keyof typeof NAMES;
interface Connections { checkedAt: string; services: { id: ServiceId; name: string; status: Status; message: string }[] }
type Source = (signal: AbortSignal) => Promise<unknown>;

export function parseFilmConnections(raw: unknown): Connections {
  const invalid = (): never => { throw new Error('Invalid connection status response.'); };
  const data = raw as { checkedAt?: unknown; services?: unknown } | null;
  if (!data || typeof data.checkedAt !== 'string' || data.checkedAt.length > 32
    || !/^\d{4}-\d{2}-\d{2}T/.test(data.checkedAt) || !Number.isFinite(Date.parse(data.checkedAt))
    || !Array.isArray(data.services) || data.services.length !== 6) return invalid();
  const services = data.services.map((value: unknown) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return invalid();
    const { id, name, status, message } = value as Record<string, unknown>;
    if (typeof id !== 'string' || !Object.prototype.hasOwnProperty.call(NAMES, id)) return invalid();
    const knownId = id as ServiceId;
    const allowed = id === 'gemini' ? ['configured', 'unavailable']
      : id === 'claude' || id === 'codex' ? ['authenticated', 'configured', 'unavailable'] : ['connected', 'unavailable'];
    if (name !== NAMES[knownId] || typeof status !== 'string' || !allowed.includes(status)
      || typeof message !== 'string' || !message.trim() || message.length > 240 || /[\u0000-\u001f\u007f]/.test(message)) return invalid();
    return { id: knownId, name: NAMES[knownId], status: status as Status, message };
  });
  if (new Set(services.map(s => s.id)).size !== 6) return invalid();
  return { checkedAt: data.checkedAt, services };
}

export async function readFilmConnections(signal: AbortSignal): Promise<unknown> {
  return (await api.get<unknown>('/films/connections', { signal, timeout: 25_000 })).data;
}

export default function CharacterFilmConnections({ source = readFilmConnections }: { source?: Source } = {}) {
  const [snapshot, setSnapshot] = useState<Connections | null>(null);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const active = useRef<AbortController | null>(null);
  const check = useCallback(async () => {
    active.current?.abort();
    const controller = new AbortController(); active.current = controller;
    const current = () => !controller.signal.aborted && active.current === controller;
    setChecking(true); setError('');
    try {
      const result = parseFilmConnections(await source(controller.signal));
      if (current()) setSnapshot(result);
    } catch {
      if (current()) setError('Connection check unavailable. Saved films are unaffected.');
    } finally {
      if (current()) { setChecking(false); active.current = null; }
    }
  }, [source]);
  // https://react.dev/reference/react/useEffect#fetching-data-with-effects — discard stale completions.
  useEffect(() => {
    setSnapshot(null); void check();
    return () => { active.current?.abort(); };
  }, [check]);

  return <section className="character-film-connections" aria-label="Film connections">
    <div className="character-film-section-heading"><h2>Film connections</h2>
      <button type="button" onClick={() => void check()} disabled={checking}>Check connections</button></div>
    <p className="character-film-muted">NotebookLM is required for film planning. Optional chat provider status is not a film-readiness guarantee.</p>
    <div aria-live="polite">
      {checking && <p role="status">Checking connections…</p>}
      {error && <p className="character-film-error" role="status">{error}</p>}
      <p className="character-film-meta">Last checked: {snapshot
        ? <time dateTime={snapshot.checkedAt}>{new Date(snapshot.checkedAt).toLocaleString()}</time> : 'Not yet checked'} · Checks cached up to 60s</p>
      {snapshot && <ul>{snapshot.services.map(service => <li key={service.id}>
        <div><strong>{service.name}</strong>{' '}<span className="character-film-badge" data-status={service.status}>{LABELS[service.status]}</span></div>
        <p className="character-film-meta">{service.message}</p>
      </li>)}</ul>}
    </div>
  </section>;
}